import type * as React from "react";
import { cva, type VariantProps } from "../../lib/cva";
import { cn } from "../../lib/utils";

/** Sizing and fit for both media elements — `Video` composes it too. */
export const imageVariants = cva("", {
  variants: {
    fill: {
      true: "h-full w-full",
      width: "w-full",
      height: "h-full",
    },
    fit: {
      contain: "object-contain",
      cover: "object-cover",
      fill: "object-fill",
      none: "object-none",
      "scale-down": "object-scale-down",
    },
  },
});

export type MediaFill = NonNullable<
  VariantProps<typeof imageVariants>["fill"]
>;
export type MediaFit = NonNullable<VariantProps<typeof imageVariants>["fit"]>;

export interface ImageProps
  extends Omit<React.ComponentProps<"img">, "width" | "height"> {
  /** Fill the parent's width, height, or both. */
  fill?: MediaFill;
  /** How the bitmap fits its box. Defaults to the browser default. */
  fit?: MediaFit;
  /** Loop an opacity pulse — the splash/loading logo idiom. */
  pulse?: boolean;
  width?: number | string;
  height?: number | string;
}

/**
 * The library owns the `<img>` element so consumers never render raw media
 * primitives. `alt` is required by the DOM contract; pass `alt=""` for purely
 * decorative art.
 */
export function Image({
  fill,
  fit,
  pulse,
  className,
  alt = "",
  ...props
}: ImageProps): React.ReactElement {
  return (
    <img
      alt={alt}
      className={cn(
        imageVariants({ fill, fit }),
        pulse && "motion-safe:animate-logo-pulse",
        className,
      )}
      {...props}
    />
  );
}

export interface VideoProps
  extends Omit<React.ComponentProps<"video">, "width" | "height" | "src"> {
  /**
   * Playable video URL — pass the presigned/CDN URL directly so the browser
   * range-streams it (do NOT fetch the whole file into a blob first).
   */
  src: string;
  /** Fill the parent's width, height, or both. */
  fill?: MediaFill;
  /** How the frame fits its box. Defaults to `contain`. */
  fit?: MediaFit;
  width?: number | string;
  height?: number | string;
}

/**
 * The `<video>` counterpart of `Image`. Streams from `src` over HTTP range
 * requests; `preload="metadata"` keeps the poster cheap.
 */
export function Video({
  src,
  fill,
  fit = "contain",
  controls = true,
  className,
  ...props
}: VideoProps): React.ReactElement {
  return (
    // biome-ignore lint/a11y/useMediaCaption: captions are the caller's data
    <video
      className={cn(imageVariants({ fill, fit }), className)}
      controls={controls}
      playsInline
      preload="metadata"
      src={src}
      {...props}
    />
  );
}
